import { Link, TableCell, TableRow } from '@ksyos/design-system';
import type { EpisodeCategory } from '@common/enums';
import type { CompensationPayoutLog } from '@common/interfaces/compensationPayoutLogs';
import { ManagerAuthorization, ModificationType } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { buildName } from '@common/utils/buildName';
import { FormatCurrency } from '@frontend/components/elements/FormatCurrency';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { hasOneAuthorization } from '@frontend/utils';

type ServiceActivityCompensationPayoutsLogProps = {
    compensationPayoutsLog: CompensationPayoutLog;
    employmentId?: string;
    episodeCategory: EpisodeCategory;
    groupId?: string;
    organizationId?: string;
    serviceActivityName: string;
    serviceName: string;
};

export function ServiceActivityCompensationPayoutsLogEntry(props: ServiceActivityCompensationPayoutsLogProps) {
    const {
        compensationPayoutsLog,
        episodeCategory,
        serviceActivityName,
        serviceName,
        employmentId,
        organizationId,
        groupId,
    } = props;
    const translate = getLanguage();
    const modifierName = props.compensationPayoutsLog.modifierPractitionerId
        ? buildName(
              props.compensationPayoutsLog.modifierPractitioner?.nameGiven ?? null,
              props.compensationPayoutsLog.modifierPractitioner?.nameFamily ?? null
          )
        : 'Via query';

    const compensationPayoutLogDetails = getCompensationLogDetails();

    const message = buildMessage();

    return (
        <TableRow key={props.compensationPayoutsLog.id}>
            <TableCell>
                <NullableFormattedDate value={props.compensationPayoutsLog.created_at} />
            </TableCell>
            <TableCell>{modifierName}</TableCell>
            <TableCell>{message}</TableCell>
        </TableRow>
    );

    function getCompensationLogDetails() {
        if (compensationPayoutsLog.serviceEmploymentId) {
            return {
                authorization: ManagerAuthorization.employmentSettingsRead,
                name: translate.compensationPayoutLevels.employment,
                payoutDetailLink: `/employments/${employmentId}/personalSettings/compensationPayouts/${episodeCategory}`,
            };
        }
        if (compensationPayoutsLog.serviceOrganizationSettingId) {
            return {
                authorization: ManagerAuthorization.organizationRead,
                name: translate.compensationPayoutLevels.organization,
                payoutDetailLink: `/organizations/${organizationId}/view/compensationPayouts/${episodeCategory}`,
            };
        }
        if (compensationPayoutsLog.serviceGroupSettingId) {
            return {
                authorization: ManagerAuthorization.groupRead,
                name: translate.compensationPayoutLevels.group,
                payoutDetailLink: `/serviceGroupSettings/${compensationPayoutsLog.serviceGroupSettingId}?groupId=${groupId}`,
            };
        }

        return {
            authorization: ManagerAuthorization.servicesRead,
            name: translate.compensationPayoutLevels.service,
            payoutDetailLink: `/services/${compensationPayoutsLog.serviceId}`,
        };
    }

    function buildMessage() {
        if (
            compensationPayoutsLog.amount !== null &&
            compensationPayoutsLog.modificationType === ModificationType.insert
        ) {
            return (
                <>
                    Vergoeding {serviceActivityName} ingesteld op{' '}
                    <FormatCurrency value={compensationPayoutsLog.amount}></FormatCurrency> ({getPayoutLevel()}) vanaf{' '}
                    <NullableFormattedDate value={compensationPayoutsLog.startDate} />
                    {compensationPayoutsLog.endDate && (
                        <>
                            {' '}
                            tot <NullableFormattedDate value={compensationPayoutsLog.endDate} />
                        </>
                    )}
                </>
            );
        }

        if (
            compensationPayoutsLog.amount !== null &&
            compensationPayoutsLog.modificationType === ModificationType.update
        ) {
            return (
                <>
                    Vergoeding is gewijzigd naar <FormatCurrency value={compensationPayoutsLog.amount}></FormatCurrency>{' '}
                    op ({getPayoutLevel()}) vanaf <NullableFormattedDate value={compensationPayoutsLog.startDate} />
                    {compensationPayoutsLog.endDate && (
                        <>
                            {' '}
                            tot <NullableFormattedDate value={compensationPayoutsLog.endDate} />
                        </>
                    )}
                </>
            );
        }

        if (compensationPayoutsLog.modificationType === ModificationType.delete) {
            return (
                <>
                    Vergoeding {serviceName} verwijderd op {getPayoutLevel()}
                </>
            );
        }
    }

    function getPayoutLevel() {
        return hasOneAuthorization(compensationPayoutLogDetails.authorization) ? (
            <Link
                color="subtle"
                to={compensationPayoutLogDetails.payoutDetailLink}
                variant="link"
            >
                {compensationPayoutLogDetails.name}
            </Link>
        ) : (
            compensationPayoutLogDetails.name
        );
    }
}
